import React, { useState, useEffect } from 'react';
import { usePromocion } from 'hooks/usePromocion';
import { useAuth } from 'contexts/AuthContext';
import { catalogoService } from 'services/usuarioService';
import {
  EstadoPromocionResponse,
  ESTADO_PROMO_LABEL,
  ESTADO_PROMO_COLOR,
  EstadoPromocion,
} from 'types/promocion.types';
import { AñoEscolar, Grado, Seccion } from 'types/usuario.types';
import { documentoService } from 'services/documentoService';
import { exportarPDF } from 'utils/reporteExport';
import { htmlActa, htmlCertificado } from 'utils/documentoPdf';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// ============================================================
// PromocionPage — Promoción y Cierre de Año
// ============================================================

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const ESTADOS_MANUALES: EstadoPromocion[] = ['RETIRADO', 'TRASLADADO'];

const PromocionPage: React.FC = () => {
  const { user } = useAuth();
  const userId = user?.idUsuario ?? 0;

  const [anios, setAnios] = useState<AñoEscolar[]>([]);
  const [grados, setGrados] = useState<Grado[]>([]);
  const [secciones, setSecciones] = useState<Seccion[]>([]);
  const [anioId, setAnioId] = useState<number | ''>('');
  const [nivel, setNivel] = useState('');
  const [gradoId, setGradoId] = useState<number | ''>('');
  const [seccionId, setSeccionId] = useState<number | ''>('');
  const [cargandoCatalogos, setCargandoCatalogos] = useState(true);

  // Modal cambio estado manual
  const [modalEstado, setModalEstado] = useState<EstadoPromocionResponse | null>(null);
  const [nuevoEstado, setNuevoEstado] = useState<EstadoPromocion>('RETIRADO');
  const [obsEstado, setObsEstado] = useState('');

  // Modal de confirmación previo a ejecutar el cálculo
  const [showConfirmCalc, setShowConfirmCalc] = useState(false);

  const params = anioId !== '' && gradoId !== '' && seccionId !== ''
    ? { anioEscolarId: anioId as number, gradoId: gradoId as number, seccionId: seccionId as number }
    : null;

  const {
    estados, loading, error, calculating, calcError,
    pendienteForzar, forzarCalculo, cancelarForzar,
    calcular, registrarManual,
  } = usePromocion(params);

  const esDirector = user?.rol === 'DIRECTOR';
  const [loadingActa, setLoadingActa]           = useState(false);
  const [loadingCertificados, setLoadingCertificados] = useState(false);

  const handleDescargarActa = async () => {
    if (anioId === '' || gradoId === '' || seccionId === '') return;
    setLoadingActa(true);
    try {
      const data = await documentoService.getActaConsolidada({
        gradoId: Number(gradoId),
        seccionId: Number(seccionId),
        anioEscolarId: Number(anioId),
      });
      exportarPDF('Acta Consolidada de Evaluación', htmlActa(data));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error al generar el Acta');
    } finally {
      setLoadingActa(false);
    }
  };

  const handleCertificadosMasivo = async () => {
    if (anioId === '' || gradoId === '' || seccionId === '') return;
    setLoadingCertificados(true);
    try {
      const lista = await documentoService.getCertificadosMasivo({
        anioEscolarId: Number(anioId),
        gradoId:       Number(gradoId),
        seccionId:     Number(seccionId),
      });
      // Filtrar entradas nulas o incompletas que pudiera devolver el backend
      const validos = lista.filter(d => d && d.matriculaId);
      if (validos.length === 0) {
        alert('No hay certificados disponibles para esta sección.');
        return;
      }
      // Concatenar todos los certificados separados por salto de página
      const htmlCombinado = validos
        .map(d => htmlCertificado(d))
        .join('<div style="page-break-after:always;height:0;margin:0;padding:0"></div>');
      const grado   = validos[0].gradoNombre   ?? '';
      const seccion = validos[0].seccionNombre ?? '';
      const anio    = validos[0].anio          ?? '';
      exportarPDF(
        `Certificados de Estudios — ${grado} Sec. ${seccion} ${anio}`,
        htmlCombinado,
        'portrait',
      );
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error al generar los certificados');
    } finally {
      setLoadingCertificados(false);
    }
  };

  useEffect(() => {
    const cargar = async () => {
      setCargandoCatalogos(true);
      try {
        const [a, g, s] = await Promise.all([
          catalogoService.getAñosEscolares(),
          catalogoService.getGrados(),
          catalogoService.getSecciones(),
        ]);
        setAnios(a);
        setGrados(g);
        setSecciones(s);
        if (a.length > 0) setAnioId(a[0].idAnioEscolar);
      } finally {
        setCargandoCatalogos(false);
      }
    };
    cargar();
  }, []);

  const niveles = Array.from(new Set(grados.map(g => g.nivel))).sort();
  const gradosFiltrados = nivel ? grados.filter(g => g.nivel === nivel) : grados;
  const seccionesFiltradas = secciones;

  // Abrir modal de confirmación (no ejecuta el cálculo aún)
  const handlePedirConfirmacion = () => {
    if (anioId === '' || gradoId === '' || seccionId === '') return;
    setShowConfirmCalc(true);
  };

  // Ejecuta el cálculo real (después de confirmar)
  const handleCalcular = async () => {
    if (anioId === '' || gradoId === '' || seccionId === '') return;
    setShowConfirmCalc(false);
    await calcular({
      gradoId: Number(gradoId),
      seccionId: Number(seccionId),
      anioEscolarId: Number(anioId),
      usuarioId: userId,
    });
  };

  const handleRegistrarManual = async () => {
    if (!modalEstado || anioId === '') return;
    await registrarManual({
      matriculaId: modalEstado.matriculaId,
      anioEscolarId: Number(anioId),
      estado: nuevoEstado,
      observacion: obsEstado || undefined,
      usuarioId: userId,
    });
    setModalEstado(null);
    setObsEstado('');
  };

  // Resumen
  const total         = estados.length;
  const promovidos    = estados.filter(e => e.estado === 'PROMOVIDO').length;
  const recuperacion  = estados.filter(e => e.estado === 'EN_RECUPERACION').length;
  const repitentes    = estados.filter(e => e.estado === 'REPITENTE').length;
  const retirados     = estados.filter(e => e.estado === 'RETIRADO' || e.estado === 'TRASLADADO').length;

  if (cargandoCatalogos) {
    return <div className="flex justify-center py-20"><SpinnerIcon /></div>;
  }

  return (
    <div className="space-y-5">
      {/* Cabecera */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold">Promoción y Cierre de Año</h1>
        <p className="text-blue-200 mt-1">Calcula y gestiona el estado de promoción por sección</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Año Escolar</label>
            <select
              value={anioId}
              onChange={e => setAnioId(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Seleccionar año</option>
              {anios.map(a => (
                <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Nivel</label>
            <select
              value={nivel}
              onChange={e => { setNivel(e.target.value); setGradoId(''); setSeccionId(''); }}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los niveles</option>
              {niveles.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Grado</label>
            <select
              value={gradoId}
              onChange={e => { setGradoId(e.target.value === '' ? '' : Number(e.target.value)); setSeccionId(''); }}
              disabled={!nivel}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="">Seleccionar grado</option>
              {gradosFiltrados.map(g => (
                <option key={g.idGrado} value={g.idGrado}>{g.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Sección</label>
            <select
              value={seccionId}
              onChange={e => setSeccionId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={gradoId === ''}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="">Seleccionar sección</option>
              {seccionesFiltradas.map(s => (
                <option key={s.idSeccion} value={s.idSeccion}>{s.nombre}</option>
              ))}
            </select>
          </div>
        </div>
        <button
          onClick={handlePedirConfirmacion}
          disabled={calculating || anioId === '' || gradoId === '' || seccionId === ''}
          className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-xl px-5 py-2.5 transition-colors disabled:opacity-60"
        >
          {calculating ? <SpinnerIcon /> : (
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z" clipRule="evenodd"/>
            </svg>
          )}
          Calcular Promoción
        </button>
      </div>

      {/* Errores */}
      {(error || calcError) && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">
          {error || calcError}
        </div>
      )}

      {/* Resumen Cards */}
      {estados.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: 'Total', valor: total, cls: 'bg-slate-100 text-slate-700' },
            { label: 'Promovidos', valor: promovidos, cls: 'bg-green-100 text-green-700' },
            { label: 'En Recuperación', valor: recuperacion, cls: 'bg-amber-100 text-amber-700' },
            { label: 'Repitentes', valor: repitentes, cls: 'bg-red-100 text-red-700' },
            { label: 'Retirados/Trasladados', valor: retirados, cls: 'bg-slate-100 text-slate-500' },
          ].map(card => (
            <div key={card.label} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 text-center">
              <p className="text-xs font-bold text-slate-500 uppercase mb-1">{card.label}</p>
              <span className={`text-2xl font-bold px-3 py-1 rounded-xl inline-block ${card.cls}`}>
                {card.valor}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Tabla */}
      {estados.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
            <div>
              <h2 className="font-bold text-slate-800">Resultados</h2>
              <p className="text-slate-400 text-xs mt-0.5">{estados.length} estudiantes</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDescargarActa}
                disabled={loadingActa}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl px-4 py-2 transition-colors disabled:opacity-60"
              >
                {loadingActa ? <SpinnerIcon /> : (
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M10 3a.75.75 0 0 1 .75.75v8.614l2.955-3.129a.75.75 0 1 1 1.09 1.03l-4.25 4.5a.75.75 0 0 1-1.09 0l-4.25-4.5a.75.75 0 1 1 1.09-1.03L9.25 12.364V3.75A.75.75 0 0 1 10 3ZM3.5 15.75a.75.75 0 0 1 .75-.75h11.5a.75.75 0 0 1 0 1.5H4.25a.75.75 0 0 1-.75-.75Z" clipRule="evenodd"/>
                  </svg>
                )}
                Acta Consolidada
              </button>
              <button
                onClick={handleCertificadosMasivo}
                disabled={loadingCertificados}
                title="Generar certificados de estudios para todos los estudiantes de la sección"
                className="flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white text-sm font-semibold rounded-xl px-4 py-2 transition-colors disabled:opacity-60"
              >
                {loadingCertificados ? <SpinnerIcon /> : (
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M4 4a2 2 0 0 1 2-2h4.586A2 2 0 0 1 12 2.586L15.414 6A2 2 0 0 1 16 7.414V16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4Zm2 6a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1Zm1 3a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2H7Z" clipRule="evenodd"/>
                  </svg>
                )}
                Certificados
              </button>
            </div>
          </div>
          {loading ? (
            <div className="flex justify-center py-12"><SpinnerIcon /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    {['Puesto', 'Estudiante', 'DNI', 'Áreas Desap.', 'Promedio Anual', 'Estado', 'Observación', 'Acción'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {estados.map((e, idx) => (
                    <tr key={e.idEstadoPromocion} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 text-sm font-bold text-slate-600">
                        {e.puesto ?? (idx + 1)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-slate-800">
                          {e.estudianteApellidos}, {e.estudianteNombres}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">{e.estudianteDni}</td>
                      <td className="px-4 py-3 text-sm font-bold text-center">
                        <span className={e.areasDesaprobadas > 0 ? 'text-red-600' : 'text-green-600'}>
                          {e.areasDesaprobadas}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {e.promedioAnual !== null ? (
                          <span className={`text-sm font-bold ${e.promedioAnual >= 11 ? 'text-green-700' : 'text-red-600'}`}>
                            {e.promedioAnual.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${ESTADO_PROMO_COLOR[e.estado]}`}>
                          {ESTADO_PROMO_LABEL[e.estado]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500 max-w-[180px] truncate" title={e.observacion ?? ''}>
                        {e.observacion || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => { setModalEstado(e); setNuevoEstado('RETIRADO'); setObsEstado(''); }}
                          className="text-xs font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          Cambiar estado
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal cambio de estado */}
      {modalEstado && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4">
              <h3 className="text-white font-bold">Cambiar Estado de Promoción</h3>
              <p className="text-blue-200 text-xs mt-0.5 truncate">
                {modalEstado.estudianteApellidos}, {modalEstado.estudianteNombres}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Nuevo Estado</label>
                <select
                  value={nuevoEstado}
                  onChange={e => setNuevoEstado(e.target.value as EstadoPromocion)}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {ESTADOS_MANUALES.map(est => (
                    <option key={est} value={est}>{ESTADO_PROMO_LABEL[est]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">
                  Observación (opcional)
                </label>
                <textarea
                  rows={3}
                  value={obsEstado}
                  onChange={e => setObsEstado(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Motivo del cambio de estado..."
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-between">
              <button
                onClick={() => setModalEstado(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleRegistrarManual}
                disabled={calculating}
                className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-xl px-5 py-2 transition-colors disabled:opacity-60"
              >
                {calculating && <SpinnerIcon />}
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Diálogo: período aún no concluido (solo DIRECTOR puede forzar) ── */}
      {pendienteForzar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-5 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-amber-600">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-base">Período aún no finalizado</h3>
                <p className="text-slate-600 text-sm mt-1">{pendienteForzar.mensaje}</p>
              </div>
            </div>
            <div className="px-6 py-5">
              {esDirector ? (
                <p className="text-slate-600 text-sm">
                  Como Director puedes forzar el cálculo aunque el período no haya concluido.
                  Los resultados podrían estar incompletos.
                </p>
              ) : (
                <p className="text-slate-600 text-sm">
                  Solo el Director puede forzar el cálculo antes de que finalice el período.
                </p>
              )}
            </div>
            <div className="px-6 pb-5 flex gap-3 justify-end">
              <button
                onClick={cancelarForzar}
                className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold
                           text-slate-600 hover:bg-slate-50 transition-colors">
                Cancelar
              </button>
              {esDirector && (
                <button
                  onClick={forzarCalculo}
                  disabled={calculating}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl bg-amber-600
                             hover:bg-amber-700 text-white text-sm font-bold transition-colors
                             disabled:opacity-60">
                  {calculating && (
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
                    </svg>
                  )}
                  Sí, calcular igualmente
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de confirmación de cálculo de promoción ── */}
      {showConfirmCalc && (() => {
        const anioSel    = anios.find(a => a.idAnioEscolar === anioId);
        const gradoSel   = grados.find(g => g.idGrado === gradoId);
        const seccionSel = secciones.find(s => s.idSeccion === seccionId);
        const esRecup    = anioSel?.fase === 'RECUPERACION';
        const totalActual = estados.length;
        return (
          <div className="modal-backdrop" onClick={() => setShowConfirmCalc(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
              <div className="modal-head">
                <div>
                  <h2>Confirmar cálculo de promoción</h2>
                  <div className="sub">Esta acción evaluará a todos los estudiantes de la sección</div>
                </div>
                <button onClick={() => setShowConfirmCalc(false)} className="sae-icon-btn">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                    <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"/>
                  </svg>
                </button>
              </div>

              <div className="modal-body">
                {/* Resumen de la selección */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="p-3 rounded-r-sm" style={{ background: 'var(--surface-2)' }}>
                    <p className="text-[11px] muted m-0 uppercase font-bold tracking-wide">Año escolar</p>
                    <p className="text-[14px] font-bold text-ink-900 m-0 mt-1">
                      {anioSel ? fmtAnioEscolar(anioSel) : '—'}
                    </p>
                  </div>
                  <div className="p-3 rounded-r-sm" style={{ background: 'var(--surface-2)' }}>
                    <p className="text-[11px] muted m-0 uppercase font-bold tracking-wide">Grado</p>
                    <p className="text-[14px] font-bold text-ink-900 m-0 mt-1">{gradoSel?.nombre ?? '—'}</p>
                  </div>
                  <div className="p-3 rounded-r-sm" style={{ background: 'var(--surface-2)' }}>
                    <p className="text-[11px] muted m-0 uppercase font-bold tracking-wide">Sección</p>
                    <p className="text-[14px] font-bold text-ink-900 m-0 mt-1">{seccionSel?.nombre ?? '—'}</p>
                  </div>
                </div>

                {/* Qué hace el proceso */}
                <div className="mb-5">
                  <h3 className="text-[13px] font-bold text-ink-900 m-0 mb-2">¿Qué hace este proceso?</h3>
                  <p className="text-[13px] text-ink-700 leading-relaxed m-0">
                    El sistema evalúa las <strong>notas finales</strong> de cada estudiante y calcula
                    automáticamente su <strong>estado de promoción</strong> según los umbrales
                    configurados.
                  </p>
                </div>

                {/* Resultados posibles */}
                <div className="mb-5">
                  <h3 className="text-[13px] font-bold text-ink-900 m-0 mb-2">Estados resultantes</h3>
                  <div className="grid gap-2">
                    <div className="row p-2.5 rounded-r-sm" style={{ background: '#dcfce7', gap: 10 }}>
                      <span className="badge success" style={{ flexShrink: 0 }}>PROMOVIDO</span>
                      <span className="text-[12.5px]" style={{ color: '#166534' }}>
                        Sin áreas desaprobadas — pasa al siguiente grado
                      </span>
                    </div>
                    {!esRecup && (
                      <div className="row p-2.5 rounded-r-sm" style={{ background: '#fef3c7', gap: 10 }}>
                        <span className="badge warning" style={{ flexShrink: 0 }}>EN_RECUPERACIÓN</span>
                        <span className="text-[12.5px]" style={{ color: '#92400e' }}>
                          Hasta 3 áreas desaprobadas (configurable) — debe matricularse en año de recuperación
                        </span>
                      </div>
                    )}
                    <div className="row p-2.5 rounded-r-sm" style={{ background: '#fee2e2', gap: 10 }}>
                      <span className="badge danger" style={{ flexShrink: 0 }}>REPITENTE</span>
                      <span className="text-[12.5px]" style={{ color: '#991b1b' }}>
                        {esRecup
                          ? 'No supera la recuperación — repite el grado'
                          : '4 o más áreas desaprobadas — repite el grado'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Aviso fase recuperación */}
                {esRecup && (
                  <div
                    className="rounded-r-sm p-3 mb-5"
                    style={{ background: 'var(--info-bg)', border: '1px solid #93c5fd' }}
                  >
                    <p className="text-[12.5px] m-0" style={{ color: '#1e40af' }}>
                      <strong>Año de recuperación:</strong> en este período solo hay dos resultados
                      posibles — <strong>PROMOVIDO</strong> o <strong>REPITENTE</strong>.
                      Los estudiantes vienen del estado <strong>EN_RECUPERACIÓN</strong> del año regular.
                    </p>
                  </div>
                )}

                {/* Advertencia */}
                <div
                  className="rounded-r-sm p-3 flex gap-2.5"
                  style={{ background: 'var(--warning-bg)', border: '1px solid #fcd34d' }}
                >
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--warning)' }}>
                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 6a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 6Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
                  </svg>
                  <div>
                    <p className="text-[13px] font-bold m-0" style={{ color: '#92400e' }}>
                      Esta acción sobrescribe estados anteriores
                    </p>
                    <p className="text-[12px] m-0 mt-0.5" style={{ color: '#92400e' }}>
                      {totalActual > 0
                        ? `Hay ${totalActual} estado${totalActual !== 1 ? 's' : ''} previamente calculado${totalActual !== 1 ? 's' : ''} para esta sección. Serán reemplazados.`
                        : 'No hay cálculos previos para esta sección. Se crearán nuevos registros para todos los estudiantes.'}
                    </p>
                    <p className="text-[12px] m-0 mt-1" style={{ color: '#92400e' }}>
                      Los estados marcados manualmente como <strong>RETIRADO</strong> o <strong>TRASLADADO</strong> NO se modifican.
                    </p>
                  </div>
                </div>
              </div>

              <div className="modal-foot">
                <button onClick={() => setShowConfirmCalc(false)} className="btn btn-ghost">
                  Cancelar
                </button>
                <button onClick={handleCalcular} className="btn btn-primary">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
                  </svg>
                  Sí, calcular promoción
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default PromocionPage;
